import { type FC, useMemo } from "react";
import { NumericFormat, type NumericFormatProps } from "react-number-format";

import { forwardRef } from "../../utils/jsx/forwardRef.ts";
import { TextField, type TextFieldProps } from "../TextField/TextField.tsx";

export type NumberFormatFieldProps = Omit<
  NumericFormatProps,
  "disabled" | "readonly" | "required" | "customInput" | "displayType" | "renderText"
> &
  Pick<
    TextFieldProps,
    | "label"
    | "isDisabled"
    | "isInvalid"
    | "isRequired"
    | "isReadOnly"
    | "errorMessage"
    | "extraAction"
    | "icon"
    | "description"
    | "noBorder"
  >;

/**
 * This component is based on TextField and React number format.
 * Check also the documentation at https://s-yadav.github.io/react-number-format/.
 */
export const NumberFormatField: FC<NumberFormatFieldProps> = (props) => {
  const customInput = useMemo(() => {
    return forwardRef<
      HTMLInputElement,
      Omit<TextFieldProps, "onChange"> & { onChange: NumericFormatProps["onChange"] }
    >(function CustomInput(props, ref) {
      const { onChange, ...rest } = props;
      return <TextField {...rest} ref={ref} onBaseChange={onChange} />;
    });
  }, []);

  return (
    <NumericFormat
      {...props}
      disabled={props.isDisabled}
      readOnly={props.isReadOnly}
      required={props.isRequired}
      spellCheck={props.spellCheck?.toString()}
      customInput={customInput}
    />
  );
};
